/*
Fork addition: builds the full-name-space CoiCatalog that
scripts/coi-import.ts converts against, from the app's runtime
GameData. At runtime every name (items, recipes, products) is a
SHORT key (see data/coi/keys.json: full name -> short key), so this
builder translates short -> full through the reverse of that map;
names with no key entry fall back to themselves.
*/
import type {GameData} from './data';
import type {CoiCatalog, CoiCatalogRecipe} from './coi-import';
//fork addition: the repo's full->short key map (bundled at build time)
import keysData from '../../../data/coi/keys.json';

const fullToShort = (keysData as {keys: Record<string, string>}).keys;
const shortToFull = new Map<string, string>();
for(const [full, short] of Object.entries(fullToShort))
    shortToFull.set(short, full);

function toFull(shortName: string): string {
    return shortToFull.get(shortName) ?? shortName;
}

export function buildCoiCatalog(gameData: GameData): CoiCatalog {
    const factories = new Map<string, {recipes: CoiCatalogRecipe[]}>();
    for(const item of gameData.gameFactoriesArray) {
        const dictionary = gameData.getItemRecipeDictionary(item);
        if(!dictionary.recipes.length)
            continue;
        const recipes: CoiCatalogRecipe[] = dictionary.recipes.map((recipe) => ({
            name: toFull(recipe.name),
            inputs: recipe.input.map((io) => toFull(io.name)),
            outputs: recipe.output.map((io) => toFull(io.name)),
        }));
        factories.set(toFull(item.name), {recipes});
    }

    const keyOf = (fullName: string): string | undefined => fullToShort[fullName];
    const productType = (fullName: string): number | undefined => {
        const short = keyOf(fullName) ?? fullName;
        return gameData.getGameItem(short)?.type;
    };
    const defaultRecipeOf = (itemFullName: string): CoiCatalogRecipe | undefined => factories.get(itemFullName)?.recipes[0];

    return {factories, keyOf, productType, defaultRecipeOf};
}
